// 순수 유틸 함수: DrawManager 관련 (React 의존성 없음)
// src/utils/lottoUtils.ts

import type { DrawRecord, LottoApiResponse, ExcelPreviewRow } from '@/pages/admin/components/draw/types';
import * as XLSX from 'xlsx';

// ---- 상수 ----

export const AUTO_SYNC_STORAGE_KEY = 'lotto_auto_sync_enabled';
export const DHLOTTERY_API = 'https://www.dhlottery.co.kr/common.do?method=getLottoNumber&drwNo=';

// ---- 날짜 유틸 ----

export function getLatestExpectedRound(): number {
  const firstDraw = new Date('2002-12-07');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const daysSinceSaturday = today.getDay() === 6 ? 0 : (today.getDay() + 1) % 7;
  const lastSaturday = new Date(today);
  lastSaturday.setDate(today.getDate() - daysSinceSaturday);

  const diffMs = lastSaturday.getTime() - firstDraw.getTime();
  return Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000)) + 1;
}

export function getDrawDateFromRound(round: number): string {
  const firstDraw = Date.UTC(2002, 11, 7);
  const targetMs = firstDraw + (round - 1) * 7 * 24 * 60 * 60 * 1000;
  const targetDate = new Date(targetMs);
  const y = targetDate.getUTCFullYear();
  const m = String(targetDate.getUTCMonth() + 1).padStart(2, '0');
  const d = String(targetDate.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function excelSerialToDate(serial: number): string {
  const utcDays = Math.floor(serial - 25569);
  const date = new Date(utcDays * 86400 * 1000);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ---- 파싱 유틸 ----

export function parseDateValue(val: unknown): string | null {
  if (typeof val === 'number') {
    if (val > 10000 && val < 60000) {
      return excelSerialToDate(val);
    }
    return String(val);
  }
  if (typeof val === 'string') {
    const trimmed = val.trim();
    const match = trimmed.match(/(\d{4})[.\-/](\d{1,2})[.\-/](\d{1,2})/);
    if (match) {
      return `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}`;
    }
    return trimmed;
  }
  return null;
}

export function parseNumbers(str: string): number[] | null {
  const nums = str.split(/[,\s]+/).map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n));
  if (nums.length !== 6) return null;
  if (nums.some((n) => n < 1 || n > 45)) return null;
  if (new Set(nums).size !== 6) return null;
  return nums.sort((a, b) => a - b);
}

export function parsePrizeValue(val: unknown): number | null {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const cleaned = val.replace(/[,원\s]/g, '');
    const num = parseInt(cleaned, 10);
    return isNaN(num) ? null : num;
  }
  return null;
}

export function parseWinnersValue(val: unknown): number | null {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const cleaned = val.replace(/[명\s,]/g, '');
    const num = parseInt(cleaned, 10);
    return isNaN(num) ? null : num;
  }
  return null;
}

// ---- 엑셀 파싱 ----

export function parseExcelFile(file: File): Promise<ExcelPreviewRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        const rows: ExcelPreviewRow[] = [];
        const failedRows: number[] = [];

        const arrayData = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' });

        if (arrayData.length >= 2) {
          const headerRow = arrayData[0];
          const hasRoundHeader = headerRow.some((h) => String(h).includes('회차'));
          const hasWinningHeader = headerRow.some((h) => String(h).includes('당첨번호'));

          if (hasRoundHeader && hasWinningHeader) {
            for (let rowIdx = 1; rowIdx < arrayData.length; rowIdx++) {
              const row = arrayData[rowIdx];
              if (!Array.isArray(row) || row.length < 9) {
                failedRows.push(rowIdx);
                continue;
              }

              const values = row.map((v) => String(v).trim());
              if (values.every((v) => v === '')) continue;

              const roundStr = values[1].replace(/,/g, '');
              const round = parseInt(roundStr, 10);
              if (isNaN(round) || round < 1) {
                failedRows.push(rowIdx);
                continue;
              }

              const nums: number[] = [];
              for (let i = 2; i <= 7; i++) {
                const n = parseInt(values[i], 10);
                if (!isNaN(n) && n >= 1 && n <= 45) {
                  nums.push(n);
                }
              }
              if (nums.length !== 6 || new Set(nums).size !== 6) {
                failedRows.push(rowIdx);
                continue;
              }

              const bonus = parseInt(values[8], 10);
              if (isNaN(bonus) || bonus < 1 || bonus > 45 || nums.includes(bonus)) {
                failedRows.push(rowIdx);
                continue;
              }

              const winners = parseWinnersValue(values[10] || '');
              const prize = parsePrizeValue(values[11] || '');
              const drawDate = getDrawDateFromRound(round);

              rows.push({
                id: round,
                draw_date: drawDate,
                numbers: nums.sort((a, b) => a - b),
                bonus,
                prize_1st: prize,
                winners_1st: winners,
              });
            }
          }
        }

        // Fallback to generic format
        if (rows.length === 0) {
          const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });

          for (const row of jsonData) {
            const keys = Object.keys(row);
            const values = Object.values(row);

            let roundIdx = -1;
            let dateIdx = -1;
            let numIndices: number[] = [];
            let bonusIdx = -1;

            for (let i = 0; i < keys.length; i++) {
              const k = keys[i].toString().toLowerCase().replace(/\s+/g, '');
              if (k.includes('회차') || k.includes('차수') || k.includes('drwno') || k.includes('round')) {
                roundIdx = i;
              } else if (k.includes('추첨') || k.includes('날짜') || k.includes('일자') || k.includes('date')) {
                dateIdx = i;
              } else if (k.includes('보너스') || k.includes('bonus') || k.includes('bnus')) {
                bonusIdx = i;
              } else if (k.includes('당첨') || k.includes('번호') || k.includes('drwt') || k.includes('num')) {
                numIndices.push(i);
              }
            }

            if (roundIdx === -1 && values.length >= 8) {
              roundIdx = 0;
              dateIdx = 1;
              numIndices = [2, 3, 4, 5, 6, 7];
              if (values.length >= 9) bonusIdx = 8;
            }

            if (roundIdx === -1 || dateIdx === -1) continue;

            const round = parseInt(String(values[roundIdx]), 10);
            if (isNaN(round) || round < 1) continue;

            const dateStr = parseDateValue(values[dateIdx]);
            if (!dateStr) continue;

            if (numIndices.length < 6) {
              numIndices = [];
              for (let i = 2; i < Math.min(values.length, 8); i++) {
                const n = parseInt(String(values[i]), 10);
                if (!isNaN(n) && n >= 1 && n <= 45) {
                  numIndices.push(i);
                }
              }
            }

            if (numIndices.length < 6) continue;

            const nums = numIndices.slice(0, 6).map((i) => parseInt(String(values[i]), 10));
            if (nums.some((n) => isNaN(n) || n < 1 || n > 45)) continue;
            if (new Set(nums).size !== 6) continue;

            let bonus = 0;
            if (bonusIdx >= 0) {
              bonus = parseInt(String(values[bonusIdx]), 10);
            } else if (values.length > numIndices[numIndices.length - 1] + 1) {
              bonus = parseInt(String(values[numIndices[5] + 1]), 10);
            }

            if (isNaN(bonus) || bonus < 1 || bonus > 45 || nums.includes(bonus)) continue;

            nums.sort((a, b) => a - b);

            rows.push({
              id: round,
              draw_date: dateStr,
              numbers: nums,
              bonus,
              prize_1st: null,
              winners_1st: null,
            });
          }
        }

        if (rows.length === 0) {
          reject(new Error('엑셀에서 유효한 당첨번호 데이터를 찾을 수 없습니다. 컬럼 구조를 확인해주세요.'));
        } else {
          rows.sort((a, b) => a.id - b.id);
          resolve(rows);
        }
      } catch (err) {
        reject(new Error(`엑셀 파싱 오류: ${String(err)}`));
      }
    };
    reader.onerror = () => reject(new Error('파일을 읽을 수 없습니다.'));
    reader.readAsArrayBuffer(file);
  });
}

// ---- API 호출 (DEPRECATED: 브라우저 직접 호출은 CORS 문제 발생) ----
// ※ 이 함수들은 CORS 제한으로 브라우저에서 직접 사용할 수 없습니다.
//    대신 useDrawManager.ts의 callApiBatch()가 Supabase Edge Function을 통해
//    서버사이드에서 동행복권 API를 호출합니다.
//    (supabase/functions/fetch-lotto-history/index.ts)

/**
 * @deprecated 브라우저에서 직접 호출 시 CORS 오류 발생.
 * 대신 supabase.functions.invoke('fetch-lotto-history', ...) 를 사용하세요.
 */
export async function fetchLottoRound(round: number): Promise<DrawRecord | null> {
  try {
    const response = await fetch(DHLOTTERY_API + round);
    if (!response.ok) return null;
    const data: LottoApiResponse = await response.json();
    if (data.returnValue !== 'success') return null;

    return {
      id: data.drwNo,
      draw_date: data.drwNoDate,
      numbers: [data.drwtNo1, data.drwtNo2, data.drwtNo3, data.drwtNo4, data.drwtNo5, data.drwtNo6].sort((a, b) => a - b),
      bonus: data.bnusNo,
      prize_1st: data.firstWinamnt,
      winners_1st: data.firstPrzwnerCo,
    };
  } catch {
    return null;
  }
}

/**
 * @deprecated 브라우저에서 직접 호출 시 CORS 오류 발생.
 * 대신 supabase.functions.invoke('fetch-lotto-history', ...) 를 사용하세요.
 */
export async function fetchLottoBatch(rounds: number[]): Promise<DrawRecord[]> {
  const results = await Promise.all(rounds.map((r) => fetchLottoRound(r)));
  return results.filter((r): r is DrawRecord => r !== null);
}